import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ExternalLink, Sparkles, Search, Instagram, Check } from 'lucide-react';
// `Shuffle` icon import was removed Feb 2026 along with the per-card "Swap" button.

/**
 * Zero-API Instagram hashtag deep-link. The slug strips every non-alphanumeric
 * character from the business name and lowercases it (e.g. "Domaine Chandon"
 * → "domainechandon"), then drops it into the hashtag-explore URL. Instagram's
 * `/explore/tags/` path is far more forgiving than `/{username}/` because it
 * surfaces ANY photo tagged with that string — so even when our slug doesn't
 * match the venue's actual handle, the user still lands on a relevant grid.
 *
 * Visibility is gated server-side via `has_active_instagram`. The model is
 * asked to emit `true` only for established, widely-photographed estates and
 * `false` for ultra-boutique / hidden producers — which prevents this button
 * from ever sending users to a dead hashtag for an unknown micro-vineyard.
 */
const instagramHashtagHref = (name) => {
  const slug = (name || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  if (!slug) return null;
  return `https://www.instagram.com/explore/tags/${slug}/`;
};

const SkeletonCard = () => (
  <div
    className="rounded-2xl border border-gray-200 bg-gray-50 p-4 overflow-hidden relative"
    data-testid="ai-search-skeleton-card"
  >
    <div className="space-y-3">
      <div className="h-5 w-2/3 rounded-md bg-gray-200" />
      <div className="h-3 w-full rounded-md bg-gray-200" />
      <div className="h-3 w-5/6 rounded-md bg-gray-200" />
      <div className="h-6 w-24 rounded-full bg-gray-200 mt-2" />
    </div>
    <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/60 to-transparent animate-[shimmer_1.6s_infinite]" />
  </div>
);

const THINKING_STAGES = [
  'Reading the room…',
  'Scouting tasting rooms…',
  'Cross-referencing amenities…',
  'Matching the vibe…',
  'Routing the trip…',
  'Pouring the final picks…',
];

const ThinkingStatus = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % THINKING_STAGES.length), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className="flex items-center gap-2 text-xs text-wine-700 font-medium"
      data-testid="ai-thinking-status"
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wine-500 opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-wine-600" />
      </span>
      <span key={idx} className="animate-fade-in">
        {THINKING_STAGES[idx]}
      </span>
    </div>
  );
};

/**
 * Scrolling stack of itinerary cards rendered INSIDE the sidebar card.
 *
 *   - "Concierge says" commentary banner
 *   - 3 result cards, each with:
 *       · AI-curated ★ rating + provenance phrase (e.g. "Google 4.6 · Wine Spectator 91 pts")
 *       · Visit website link (model-supplied URL) and Reviews link (Google-search fallback)
 *       · Per-card quick-actions (Swap this · More like this) — one-tap refinements
 *   - Tappable follow-up suggestion chips at the bottom
 */
const AiSearchResults = ({
  isSearching,
  results,
  submittedQuery,
  commentary,
  followUps = [],
  matchedWinery = null,
  onRefine,
  radiusSpread,
  hideFollowUps = false,
}) => {
  if (!isSearching && results.length === 0) return null;

  const triggerRefine = (prompt) => {
    if (typeof onRefine === 'function' && prompt) onRefine(prompt);
  };

  // Format the measured spread for the trust pill: ≤1mi reads as "all within 1 mi",
  // 1.1–2 reads as "all within 2 mi" (rounded up to the next whole mile so the
  // visible promise always exceeds the actual measurement — never under-sells).
  const radiusPill = (() => {
    if (isSearching || !radiusSpread || radiusSpread.status !== 'ok') return null;
    const { miles, limit } = radiusSpread;
    const display = miles <= 1 ? '1' : String(Math.ceil(miles));
    return { display, limit, miles };
  })();

  return (
    <div className="space-y-3" data-testid="ai-search-results-stack">
      {submittedQuery && (
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
            Trip Plan for
          </div>
          <div
            className="text-[11px] text-gray-500 italic truncate max-w-[70%]"
            title={submittedQuery}
            data-testid="ai-search-submitted-query"
          >
            “{submittedQuery}”
          </div>
        </div>
      )}

      {isSearching && (
        <div className="rounded-2xl bg-wine-50 border border-wine-100 px-3 py-2.5">
          <ThinkingStatus />
        </div>
      )}

      {!isSearching && commentary && (
        <div
          className="rounded-2xl bg-gradient-to-br from-gold-50 to-wine-50 border border-gold-200 p-3.5"
          data-testid="ai-commentary-banner"
        >
          <div className="flex items-start gap-2">
            <div className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-gradient-to-br from-wine-600 to-wine-700 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-gold-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-wine-700 mb-0.5">
                Concierge says
              </div>
              <p className="text-[13px] text-[#1a1a2e] leading-snug">{commentary}</p>
              <p
                className="mt-1.5 text-[10.5px] leading-snug italic text-gray-500"
                data-testid="ai-hallucination-disclaimer"
              >
                AI Concierge can hallucinate — please double-check addresses, hours, and booking details before you visit.
              </p>
            </div>
          </div>
        </div>
      )}

      {radiusPill && (
        <div
          className="flex items-center gap-1.5 self-start rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 shadow-sm"
          data-testid="ai-radius-trust-pill"
          title={`Widest pair measured ${radiusPill.miles.toFixed(1)} mi (limit ${radiusPill.limit} mi).`}
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-600 text-white">
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
          </span>
          <span>All stops within {radiusPill.display} mi</span>
        </div>
      )}

      {isSearching && (
        <>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </>
      )}

      {!isSearching &&
        results.map((r, i) => {
          // Instagram visibility is server-gated: only render when the LLM
          // explicitly flagged this venue as having an active public footprint.
          // Saved trips persisted before this field existed will be undefined
          // → !== true → button hidden, which is the safer fallback.
          const igHref = r.has_active_instagram === true ? instagramHashtagHref(r.name) : null;
          // "Top Match" — when the backend fuzzy-matched a real winery from
          // the user's typed query, the matching stop is pinned at index 0
          // by useAiSearch. Render a gold pill that anchors the user's intent
          // to the result they were searching for.
          const isTopMatch = (() => {
            if (i !== 0 || !matchedWinery) return false;
            const target = matchedWinery.toLowerCase();
            const name = (r.name || '').toLowerCase();
            return name.includes(target) || target.includes(name);
          })();
          return (
            <div
              key={`${r.name}-${i}`}
              className="group/card rounded-2xl border border-gray-200 bg-white hover:border-wine-300 hover:shadow-md transition-all p-4"
              data-testid={`ai-search-result-card-${i}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <div className="text-wine-600 text-[10px] font-bold uppercase tracking-wider">
                    Stop {i + 1}
                  </div>
                  {isTopMatch && (
                    <div
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-gold-500 to-gold-600 border border-gold-700 text-[9px] font-bold uppercase tracking-[0.12em] text-white shadow-md"
                      data-testid={`ai-card-top-match-${i}`}
                      title={`Top match for your search: "${matchedWinery}"`}
                    >
                      🎯 Your Match
                    </div>
                  )}
                </div>
                {r.editorial_badge ? (
                  <div
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-gold-100 via-gold-200 to-gold-100 border border-gold-300 text-[9px] font-bold uppercase tracking-[0.12em] text-wine-800 shadow-sm"
                    data-testid={`ai-card-editorial-badge-${i}`}
                    title="Editorial highlight, hand-picked by the AI Concierge"
                  >
                    <Sparkles className="w-2.5 h-2.5 text-gold-700" />
                    {r.editorial_badge}
                  </div>
                ) : null}
              </div>

              {r.website_url ? (
                <a
                  href={r.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/name inline-flex items-start gap-1 text-[#1a1a2e] font-semibold text-base leading-snug hover:text-wine-700 transition-colors"
                  data-testid={`ai-card-name-link-${i}`}
                  title={`Open ${r.name} website`}
                >
                  <span className="break-words underline decoration-dotted decoration-gray-300 group-hover/name:decoration-wine-500 underline-offset-4">
                    {r.name}
                  </span>
                  <ExternalLink className="w-3 h-3 mt-1 shrink-0 text-gray-400 group-hover/name:text-wine-600" />
                </a>
              ) : (
                <h3 className="text-[#1a1a2e] font-semibold text-base leading-snug break-words">
                  {r.name}
                </h3>
              )}

              {r.amenity_tag && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {r.amenity_tag
                    .split(/,\s*/)
                    .filter(Boolean)
                    .slice(0, 4)
                    .map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] font-medium uppercase tracking-wide bg-gold-100 text-wine-800 border border-gold-200 px-2 py-0.5 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                </div>
              )}
              {/* When the Instagram button is gated off, give the description a
                  touch more breathing room so the card doesn't feel light. */}
              <p
                className={`text-gray-600 text-sm mt-2 leading-relaxed break-words ${
                  igHref ? '' : 'mb-1'
                }`}
              >
                {r.vibe}
              </p>
              <div className="flex items-start gap-1.5 text-gray-400 text-xs mt-2">
                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="break-words min-w-0">{r.address}</span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1a1a2e] hover:bg-[#16213e] px-3 py-1.5 rounded-md transition-colors"
                  data-testid={`ai-search-directions-${i}`}
                >
                  Get directions <ExternalLink className="w-3 h-3" />
                </a>
                {igHref ? (
                  <a
                    href={igHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-gradient-to-r from-fuchsia-600 via-rose-500 to-amber-500 hover:from-fuchsia-700 hover:via-rose-600 hover:to-amber-600 px-2.5 py-1.5 rounded-md shadow-sm hover:shadow-md transition-all"
                    data-testid={`ai-card-instagram-${i}`}
                    title="Open the Instagram photo grid for this venue in a new tab"
                    aria-label={`Open Instagram photos for ${r.name}`}
                  >
                    <Instagram className="w-3 h-3" />
                    <span aria-hidden="true">✨</span> Explore Photos &amp; Vibes
                  </a>
                ) : null}
                {/* "Swap" per-card button removed Feb 2026 per user request.
                    Users can still re-roll a stop via the "Keep Refining"
                    follow-up grid below the itinerary list. */}
                <button
                  type="button"
                  onClick={() => triggerRefine(`Find another like ${r.name} — same character, different place`)}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-700 hover:text-white hover:bg-[#1a1a2e] bg-gray-50 border border-gray-200 hover:border-[#1a1a2e] px-2.5 py-1.5 rounded-md transition-colors"
                  data-testid={`ai-card-similar-${i}`}
                  title="Find more like this"
                >
                  <Search className="w-3 h-3" /> Similar
                </button>
              </div>
            </div>
          );
        })}

      {!isSearching && !hideFollowUps && followUps.length > 0 && (
        <div
          className="rounded-2xl bg-[#1a1a2e]/[0.03] border border-dashed border-[#1a1a2e]/15 p-3.5 mt-2"
          data-testid="ai-followup-chips"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1a1a2e]/70 mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-wine-600" />
            Keep refining — tap any
          </div>
          <div className="flex flex-wrap gap-1.5">
            {followUps.map((s, i) => (
              <button
                key={s}
                type="button"
                onClick={() => triggerRefine(s)}
                className="text-[11px] sm:text-xs font-medium text-[#1a1a2e] bg-white hover:bg-wine-600 hover:text-white border border-gray-200 hover:border-wine-600 rounded-full px-3 py-1.5 transition-all hover:-translate-y-0.5 hover:shadow-sm"
                data-testid={`ai-followup-chip-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSearchResults;
